import { FormField, isolate } from '@batiflow/ui';
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  Textarea,
} from '@fluentui/react-components';
import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNotify } from '@/shared/hooks/use-notify';
import type { DocumentDetail } from './document-model';
import { usePanelStyles } from './panel-styles';
import { REASON_MAX, REASON_MIN } from './restore-dialog';
import { precheck } from './upload-model';
import { useIssueMessage } from './upload-rows';
import type { Uploads } from './use-uploads';

export interface NewVersionDialogProps {
  document: DocumentDetail;
  uploads: Uploads;
  onClose: () => void;
}

/**
 * Adds a new version: one file and the mandatory reason. The file goes through the same upload
 * pipeline as any other (progress under the folder's upload zone, antivirus check), as a new
 * version of the document.
 */
export function NewVersionDialog({ document, uploads, onClose }: NewVersionDialogProps) {
  const { t } = useTranslation('documents');
  const styles = usePanelStyles();
  const notify = useNotify();
  const issueMessage = useIssueMessage();
  const fileId = useId();
  const fileErrorId = useId();

  const [file, setFile] = useState<File | null>(null);
  const [reason, setReason] = useState('');
  const [touched, setTouched] = useState(false);

  const issue = file ? precheck(file) : null;
  const fileError =
    touched && !file ? t('newVersion.fileRequired') : issue ? issueMessage(issue) : null;
  const reasonError = reason.trim().length < REASON_MIN ? t('reason.required') : null;

  const submit = () => {
    setTouched(true);
    if (!file || issue || reasonError) return;
    uploads.controller.addFiles(
      [file],
      { spaceId: document.spaceId, folderId: document.folderId, documentId: document.id },
      [{ revisionReason: reason.trim() }],
    );
    notify({
      intent: 'info',
      message: t('newVersion.started', { name: isolate(file.name) }),
    });
    onClose();
  };

  return (
    <Dialog
      open
      modalType="modal"
      onOpenChange={(_event, data) => {
        if (!data.open) onClose();
      }}
    >
      <DialogSurface>
        <DialogBody>
          <DialogTitle>{t('newVersion.title')}</DialogTitle>
          <DialogContent>
            <div className={styles.dialogBody}>
              <p>{t('newVersion.intro', { title: isolate(document.title) })}</p>
              <div className={styles.fileField}>
                <label htmlFor={fileId} className={styles.fileLabel}>
                  {t('newVersion.file')}
                </label>
                <input
                  id={fileId}
                  type="file"
                  required
                  aria-invalid={fileError ? true : undefined}
                  aria-describedby={fileError ? fileErrorId : undefined}
                  onChange={(event) => {
                    setFile(event.target.files?.[0] ?? null);
                  }}
                />
                {fileError ? (
                  <span id={fileErrorId} role="alert" className={styles.fileError}>
                    {fileError}
                  </span>
                ) : null}
              </div>
              <FormField
                label={t('reason.label')}
                required
                hint={t('reason.hint', { max: REASON_MAX })}
                error={touched ? reasonError : null}
              >
                <Textarea
                  className={styles.textarea}
                  maxLength={REASON_MAX}
                  value={reason}
                  onChange={(_event, data) => {
                    setReason(data.value);
                  }}
                  onBlur={() => {
                    setTouched(true);
                  }}
                />
              </FormField>
            </div>
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={onClose}>
              {t('common:actions.cancel')}
            </Button>
            <Button appearance="primary" onClick={submit}>
              {t('newVersion.confirm')}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}
